import { inject, Injectable } from '@angular/core';
import { doc, docData, Firestore } from '@angular/fire/firestore';
import { Data } from '@angular/router';
import { catchError, EMPTY, filter, interval, map, Observable, of, retry, switchMap, take, takeUntil, throwError, timer, tap } from 'rxjs';
import { PurchaseDetail } from './card-upsell-service';
import { CashPurchaseService } from 'src/app/services/cash-purchase.service';
import { CommonService } from 'src/app/services/common/common.service';
import { CommonVariable } from 'src/app/app.constants';
import { PaymentService } from 'src/app/services/payment.service';
import { AuthService } from '@core/auth.service';
import type { PaymentPage } from '../payment.page';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { GTMSagmentService } from 'src/app/services/gtm-sagment.service';
import { GTMSegmentService } from 'src/app/services/gtm-segment.service';
import { BasePaymentService } from './base-payment-service';
import { PaymentNavigationService } from './payment-navigation-service';

interface PaymentStatusResult {
  type: 'success' | 'error';
  payload?: any;
  payment_status?: any;
}

@Injectable()
export class PaymentFlowService {
  private firestore = inject(Firestore);
  private cashPurchaseService = inject(CashPurchaseService);
  private commonService = inject(CommonService);
  private paymentService = inject(PaymentService);
  private authService = inject(AuthService);
  private angularFireAuth = inject(AngularFireAuth);
  private gtmService = inject(GTMSagmentService);
  private gtmSegment = inject(GTMSegmentService);
  private basePaymentService = inject(BasePaymentService);
  private paymentNavigationService = inject(PaymentNavigationService);

  private readonly regexSuccess = /^(000.000.|000.100.1|000.[36]|000.400.[1][12]0)/;
  private readonly regexSuccessManualValidation = /^(000.400.0[^3]|000.400.100)/;

  pollPaymentStatus(encryptedCustomerPurchaseId: string) {
    // Guard: an absent or empty ID would produce an invalid Firestore path on the
    // immediate t=0 tick from timer(0, pollInterval), causing an unhandled error.
    if (!encryptedCustomerPurchaseId) {
      return of(null);
    }
    

    const timeout$ = timer(20000);
    const pollInterval = 2000;

    return (this.commonService.getUid('') || of(null)).pipe(
      switchMap((userId) => {
        if (!userId) return of(null);

        const docRef = doc(this.firestore, `users/${userId}/customer_purchase/${encryptedCustomerPurchaseId}`);

        return timer(0, pollInterval)
          .pipe(
            switchMap(() => {
              return docData(docRef).pipe(
                catchError(() => {
                  return of(null);
                })
              );
            }),
            map((res) => {
              // Polling can begin before the charge request is sent. Descriptions
              // such as "Approved" or "Processing" do not prove fulfilment or
              // failure; only the original purchase's terminal server status does.
              const status = res?.processed_status;
              if (status === 2 || status === "2") {
                return { success: true, data: res };
              } else if (status === -1 || status === "-1") {
                return { success: false, data: res };
              }
              return null;
            }),
            filter((res) => res != null),
            take(1),
            takeUntil(timeout$),
            catchError(() => {
              return of(null);
            })
          )
      }),
      catchError(() => of(null))
    )
  }

  // Process payment with card
  proceedToPay(config: {
    purchaseDetail: Data;
    altprosubDetails: Data;
    selectedStoredCard: Data | null;
    one_click_pay: boolean;
    processcustomer_offer: string;
    canSubmit?: () => boolean;
    onBeforeCardPayment?: (encryptedCustomerPurchaseId: string) => void | boolean;
    onCardPaymentNotInvoked?: () => void;
    prosubId?: string;
  }) {
    const {
      purchaseDetail,
      altprosubDetails,
      selectedStoredCard,
      one_click_pay,
      processcustomer_offer
    } = config;
    const ownerProsubId = config.prosubId ?? localStorage.getItem("prosub_id");

    const resolvedStoredCardId = this.resolveStoredCardId(selectedStoredCard);
    if (!resolvedStoredCardId) {
      return throwError(() => new Error('err_missing_stored_card'));
    }

    const normalizedSelectedStoredCard = selectedStoredCard?.encrypt_card_id
      ? selectedStoredCard
      : { ...selectedStoredCard, encrypt_card_id: resolvedStoredCardId };

    return this.insert_customer_purchase(purchaseDetail, normalizedSelectedStoredCard, one_click_pay)
      .pipe(
        switchMap((insertResult) => {
          if (config.canSubmit?.() === false) return EMPTY;

          if (insertResult?.response?.status !== CommonVariable.success) {
            return of(insertResult);
          }

          if (altprosubDetails?.free_reg_mod_id === CommonVariable.prizeDrawToastCheck) {
            this.paymentService?.showPrizeDrawToastMessage();
          }

          const customer_purchase_id = insertResult?.response?.customer_purchase_id
          const encrypted_customer_purchase_id = insertResult?.response?.encrypted_customer_purchase_id;
          if (!customer_purchase_id || typeof encrypted_customer_purchase_id !== 'string' ||
              !encrypted_customer_purchase_id.trim() || /[\/\r\n]/.test(encrypted_customer_purchase_id)) {
            return throwError(() => new Error('err_invalid_purchase_request'));
          }
          this.paymentService.salesJourneyCustomerPurchaseId$.next(encrypted_customer_purchase_id);

          return this.process_customer_purchase(
            customer_purchase_id,
            processcustomer_offer,
            ownerProsubId,
          ).pipe(

            map((process_customer_purchase_result) => ({
              process_customer_purchase_result,
              customer_purchase_id: customer_purchase_id,
              encrypted_customer_purchase_id: encrypted_customer_purchase_id
            }))
          );
        }),
        switchMap(({ process_customer_purchase_result, customer_purchase_id, encrypted_customer_purchase_id }) => {

          if (process_customer_purchase_result?.status == 1) {
            if (config.canSubmit?.() === false) return EMPTY;
            // Retain the original purchase before the charge request can leave
            // the browser. A lost response must resume status reads only.
            if (config.onBeforeCardPayment?.(encrypted_customer_purchase_id) === false) return EMPTY;
            return this.process_card_payment(customer_purchase_id, encrypted_customer_purchase_id,
              normalizedSelectedStoredCard, config.canSubmit, config.onCardPaymentNotInvoked);
          }

          return of({
            process_card_payment_response: process_customer_purchase_result,
            encrypted_customer_purchase_id
          });
        }),
        catchError(error => {
          // Convert synchronous TypeErrors (e.g. undefined stream) into a named error
          // so handle_payment_error can process them cleanly instead of surfacing as unhandled
          if (error instanceof TypeError) {
            return throwError(() => new Error('err_undefined_stream'));
          }
          return throwError(() => error);
        })
      )
  }

  private resolveStoredCardId(card: Data | null): string {
    return (
      card?.encrypt_card_id ||
      card?.pay_stored_detail_id ||
      card?.customer_payment_stored_detail_id ||
      card?.customer_Payment_stored_detail_ID ||
      ''
    );
  }

  handle_payment_error(error: any) {
    if (
      error?.message?.includes('err_invalid_purchase_detail') ||
      error?.message?.includes('err_invalid_purchase_request') ||
      error?.message?.includes('err_invalid_process_payment_request') ||
      error?.message?.includes('err_undefined_stream') ||
      error?.message?.includes('where a stream was expected')
    ) {
      this._handlePaymentError('Unable to process payment. Please refresh the page and try again.');
      this.authService.errorHandlerLogs('Payment failed due to invalid purchase data', error);
      return;
    }
    if (
      error?.message?.includes('err_insert_customer_purchase') ||
      error?.message?.includes('err_process_customer_purchase') ||
      error?.message?.includes('err_process_card_payment') ||
      error?.message?.includes('err_missing_stored_card')
    ) {
      this._handlePaymentError('Unable to process payment. Please add a card or try again.');
      this.authService.errorHandlerLogs('4c stored-card payment failed', error);
      return;
    }
    return error;
  }

  checkNewCardPaymentStatus(
    paymentId: string,
    paymentMode: string,
    fourCInlineError?: (message: string) => void
  ): Observable<PaymentStatusResult> {
    return this.paymentService.checkPaymentStatus(paymentId, paymentMode).pipe(
      retry(3),
      switchMap((payment_status) => {
        if (!payment_status) {
          return this._fetchStatusFromSQL(paymentId, fourCInlineError);
        }

        return of({
          type: 'success' as const,
          payload: payment_status,
          payment_status,
        });
      }),
      catchError((err) => {
        this._handlePaymentError(err?.message, fourCInlineError);
        return of({ type: 'error' as const });
      })
    );
  }


  // AC-4: pure evaluator — regexes stay private
  public isPaymentSuccess(code: string): boolean {
    return !!(code?.match(this.regexSuccess) || code?.match(this.regexSuccessManualValidation));
  }

  // Helpers
  private insert_customer_purchase(purchaseDetail: Data, selectedStoredCard: Data, one_click_pay: boolean) {

    return this.paymentService.insertCustomerPurchase(
      { ...purchaseDetail, ...selectedStoredCard },
      'insert_customer_purchase_4c_payment',
      one_click_pay
    ).pipe(
      catchError(() => throwError(() => new Error('err_insert_customer_purchase')))
    )
  }

  private process_customer_purchase(customer_purchase_id: string, processcustomer_offer: string, prosub_id: string) {

    return this.paymentService.processcustomer_purchase(
      customer_purchase_id,
      prosub_id,
      processcustomer_offer
    ).pipe(
      catchError(() => throwError(() => new Error('err_process_customer_purchase')))
    )
  }

  private process_card_payment(
    customer_purchase_id: string,
    encrypted_customer_purchase_id: string,
    selectedStoredCard: Data,
    canSubmit?: () => boolean,
    onNotInvoked?: () => void
  ) {
    return this.paymentService.processCardPayment(
      customer_purchase_id,
      selectedStoredCard,
      "/api_post_request/process_4c_payment_existing_card",
      canSubmit,
      onNotInvoked
    ).pipe(
      map((process_card_payment_response) => ({ process_card_payment_response, encrypted_customer_purchase_id })),
      catchError(error => throwError(() => error?.code === 'CALLABLE_NOT_INVOKED'
        ? error
        : new Error('err_process_card_payment')))
    )
  }

  private _fetchStatusFromSQL(
    paymentId: string,
    fourCInlineError?: (message: string) => void
  ): Observable<PaymentStatusResult> {
    return this.paymentService.checkPaymentStatusViaSQL(paymentId).pipe(
      switchMap((payment_statusViaSQL) => {
        const response = payment_statusViaSQL?.response;
        const isSuccess =
          response?.status === 'SUCCESS' &&
          response?.payment_response?.payload;

        if (isSuccess) {
          return of({
            type: 'success' as const,
            payload: response.payment_response.payload,
            payment_status: payment_statusViaSQL,
          });
        }

        this._handlePaymentError(response?.message || 'Something went wrong!', fourCInlineError);
        return of({ type: 'error' as const });
      }),
      catchError((err) => {
        this._handlePaymentError(err?.message, fourCInlineError);
        return of({ type: 'error' as const });
      })
    );
  }

  private _handlePaymentError(message?: string, fourCInlineError?: (message: string) => void): void {
    const line1 = message || 'Something went wrong!';
    if (fourCInlineError) {
      try {
        fourCInlineError(line1);
        return;
      } catch (inlineError) {
        this.authService.errorHandlerLogs(
          'PaymentFlowService: fourCInlineError failed, falling back to toast',
          inlineError
        );
      }
    }
    try {
      const translatedMsg = {
        message: { line_1: line1, line_2: '' },
        style: {
          duration: 2000,
          icon: 'tick circle error.svg',
          type: CommonVariable.error,
        },
      };
      const defaultMsg = {
        message: { line_1: 'Something went wrong!', line_2: '' },
        style: {
          icon: 'tick circle error.svg',
          type: CommonVariable.error,
        },
      };
      this.authService.showToastMessage(translatedMsg, defaultMsg);
    } catch (toastError) {
      this.authService.errorHandlerLogs(
        'PaymentFlowService: showToastMessage failed',
        toastError
      );
    }
  }


  getPaymentSyndicateId(
    purchaseDetail: Data,
    payment_status: Data,
    customerPurchaseJSON: Data,
    ctx: PaymentPage
  ) {
    localStorage.setItem("sjFlow", "addCard");
    void ctx.callInt2();
    return this.paymentService
      .updatePaymentReqAndResNew(
        {
          ...purchaseDetail,
        },
        payment_status,
        customerPurchaseJSON,
        payment_status?.ndc,
        false
      )
            .pipe(
        map((sqlAPICall) => {
          // Log customer purchase
          ctx.logCustomerPurchase({
            payment_response: payment_status,
            payment_cp_request: {
              ...purchaseDetail,
              ...customerPurchaseJSON,
            },
            payment_success: true,
            cp_request: { ...purchaseDetail, ...ctx.getCheckCardDetailId() },
            payment_cp_response: sqlAPICall,
          });

          // Update customer purchase ID
          ctx.customer_purchase_id = sqlAPICall?.response?.customer_purchase_id
            ? sqlAPICall?.response?.customer_purchase_id
            : sqlAPICall?.response?.encrypted_customer_purchase_id;

          // Update payment service with response
          this.paymentService.paymentSqlResp$.next(sqlAPICall);

          // Handle error response
          if (sqlAPICall && sqlAPICall?.response?.status !== CommonVariable.success) {
            this.basePaymentService.showToast("1015", "Something went wrong!", "Error");
            throw new Error('err_payment_update_failed');
          }

          // Start spinner
          ctx.startSpinner();

          // Update syndicate details for THIS payment only (drop any previous MSN IDs).
          // Publish this payment's primary IDs first so subscribers never see a transient
          // null when a replacement value exists; then reset only ancillary subjects.
          const existingCpId = this.paymentService.salesJourneyCustomerPurchaseId$.getValue();
          const newSyndicateId = sqlAPICall?.response?.encrypt_syndicate_id ?? null;
          const newCpId =
            sqlAPICall?.response?.encrypted_customer_purchase_id || existingCpId || null;

          if (!ctx.customer_purchase_id && newCpId) {
            ctx.customer_purchase_id = newCpId;
          }

          if (newSyndicateId) {
            this.paymentService.salesJournyPaymentDetails$.next(newSyndicateId);
          } else {
            // This payment has no syndicate id — do not keep a leftover MSN id.
            this.paymentService.salesJournyPaymentDetails$.next(null);
          }
          if (newCpId) {
            this.paymentService.salesJourneyCustomerPurchaseId$.next(newCpId);
          }

          this.paymentService.resetAncillaryPostPaymentTicketIds();

          // Fire here with SQL purchase id when earlier gateway push was skipped
          // due to missing id/offer/price (guarded + deduped in GTM_CJ_conversion).
          this.gtmService.GTM_CJ_conversion(
            ctx.altprosubDetails?.price ??
              purchaseDetail?.prize ??
              purchaseDetail?.offers?.price ??
              purchaseDetail?.offers?.int_price?.[ctx.preferredCurrency] ??
              purchaseDetail?.offers?.int_price?.GBP,
            purchaseDetail?.offers?.currency
              ? purchaseDetail?.offers?.currency
              : ctx.preferredCurrency || "GBP",
            ctx.altprosubDetails?.offerid ??
              ctx.altprosubDetails?.offer_id ??
              purchaseDetail?.offer_id ??
              purchaseDetail?.offers?.offerid ??
              purchaseDetail?.offers?.offer_id,
            this.gtmService?.userIsNew ? "new" : "return",
            CommonVariable.countryCode,
            purchaseDetail?.p_type ? purchaseDetail?.p_type : "10",
            ctx.altprosubDetails?.prosub_id ?? purchaseDetail?.prosub_id ?? localStorage.getItem("prosub_id"),
            ctx.customer_purchase_id,
            {
              purchaseDetail,
              altprosubDetails: ctx.altprosubDetails,
              preferredCurrency: ctx.preferredCurrency || "GBP",
              paymentMethod: "CheckOut",
              brand: "App",
            }
          );
          this.gtmSegment.userProduct$.next(this.gtmSegment.userProduct$.value);

          // Handle free registration org prosub
          if (ctx.org_prosub) {
            this.paymentService.freeReg4cOrgProsub = ctx.org_prosub;
            localStorage.setItem("prosub_id", ctx.org_prosub);
          }

          return sqlAPICall;
        }),
        catchError((error) => {
          this.paymentService?.trackCustomerPurchase(payment_status?.ndc);
          return throwError(() => error instanceof Error ? error : new Error('err_payment_update_failed'));
        })
      );
  }

  private _buildNavCtx(ctx: PaymentPage) {
    return {
      Prosub_id: ctx.Prosub_id,
      TP1: ctx.TP1,
      Tp2: ctx.Tp2,
      TP3: ctx.TP3,
      T_ID: ctx.T_ID,
      upsellID: ctx.upsellID,
      timerOn: ctx.timerOn,
      spinnerAnimation: ctx.spinnerAnimation,
      isPaymentDone: ctx.isPaymentDone,
      addingCard: ctx.addingCard,
      addNewCardInProgress: ctx.addNewCardInProgress,
      getCheckCardDetailId: () => ctx.getCheckCardDetailId(),
      onStateChange: (patch: Partial<{ timerOn: boolean; spinnerAnimation: boolean; isPaymentDone: boolean; addingCard: boolean; addNewCardInProgress: boolean }>) => Object.assign(ctx, patch),
      _syndicateId: ctx.syndicateId,
    };
  }
  public handleSuccessPayment(payment_status: { [key: string]: unknown }, ctx: PaymentPage): void {
    this.paymentService?.setSyndicatePending();
    let purchaseDetail: PurchaseDetail | null = null;
    try {
      const raw = localStorage.getItem('purchaseDetailData');
      purchaseDetail = raw ? JSON.parse(raw) : null;
    } catch {
      purchaseDetail = null;
    }
    ctx.logCustomerPurchase({
      payment_response: payment_status,
      payment_success: true,
      cp_request: { ...(purchaseDetail ?? {}), ...ctx.getCheckCardDetailId() },
    });

    // Fire payment_success immediately on gateway success. Do not wait for
    // updatePaymentReqAndResNew / callInt2 — that path can fail or navigate away
    // before GTM_CJ_conversion runs.
    const immediatePurchaseId =
      ctx.customer_purchase_id ||
      this.paymentService.salesJourneyCustomerPurchaseId$.getValue() ||
      '';
    const amount =
      ctx.altprosubDetails?.price ??
      purchaseDetail?.prize ??
      purchaseDetail?.offers?.price ??
      purchaseDetail?.offers?.int_price?.[ctx.preferredCurrency] ??
      purchaseDetail?.offers?.int_price?.GBP;
    const offerId =
      ctx.altprosubDetails?.offerid ??
      ctx.altprosubDetails?.offer_id ??
      purchaseDetail?.offer_id ??
      purchaseDetail?.offers?.offerid ??
      purchaseDetail?.offers?.offer_id;
    const prosubId =
      ctx.altprosubDetails?.prosub_id ??
      purchaseDetail?.prosub_id ??
      localStorage.getItem('prosub_id');
    this.gtmService.GTM_CJ_conversion(
      amount,
      purchaseDetail?.offers?.currency
        ? purchaseDetail?.offers?.currency
        : ctx.preferredCurrency || 'GBP',
      offerId,
      this.gtmService?.userIsNew ? 'new' : 'return',
      CommonVariable.countryCode,
      purchaseDetail?.p_type ? purchaseDetail?.p_type : '10',
      prosubId,
      immediatePurchaseId,
      {
        purchaseDetail,
        altprosubDetails: ctx.altprosubDetails,
        preferredCurrency: ctx.preferredCurrency || 'GBP',
        paymentMethod: 'CheckOut',
        brand: 'App',
      }
    );
    this.gtmSegment.userProduct$.next(this.gtmSegment.userProduct$.value);

    ctx.getPaymentSyndicateId(payment_status);
    if (purchaseDetail?.prosub_id === ctx.expiredCardOfferProsubId) {
      ctx.setCardAsPrimaryMethod();
    }

    // resetting iframe states to default
    ctx.timeOut = true;
    ctx.addingCard = false;
    ctx.addNewCardInProgress = false;
    ctx.showAddNewCard = true;
    ctx.isPaymentDone = false;
    ctx.count = 0;
    ctx.progressBar = 0;
    ctx.timerOn = false;
    ctx.isCheckoutCom = false;

    // Payment feedback: 4C uses static in-page alert (mapped constants); other journeys keep toast/snackbar
    if (ctx.isFourCCheckoutRoute()) {
      if (ctx.altprosubDetails?.free_reg_mod_id === CommonVariable.prizeDrawToastCheck) {
        void ctx.showFourCAlertByConstantsCode(
          "1122",
          "Congratulations – You've upgraded and in the draw",
          "success"
        );
      } else {
        void ctx.showFourCAlertFromSyndicatePaymentStatus(payment_status);
      }
    } else if (ctx.altprosubDetails?.free_reg_mod_id === CommonVariable.prizeDrawToastCheck) {
      this.paymentService?.showPrizeDrawToastMessage();
    } else {
      this.paymentService?.showPaymentStatusToastMessage(payment_status);
    }
  }


  public handlePaymentStatusReceived(
    payment_status: { result?: { code?: string; description?: string }; [key: string]: unknown },
    paymentStatusFromCP: boolean,
    ctx: PaymentPage
  ): void {
    if (
      paymentStatusFromCP ||
      (payment_status?.result?.code && this.isPaymentSuccess(payment_status.result.code))
    ) {
      ctx.payment_done = 'success';
      ctx.isPaymentDone = false;
      ctx.paymentReceived = false;
      ctx.paymentReceivedFromCP = false;
      ctx.resetPaymentSubscriptions();
      this.angularFireAuth.currentUser
        .then((user) => {
          if (!user) {
            const token = localStorage.getItem('fbToken');
            if (token) {
              ctx.manualLoginPayment(token, payment_status);
            } else {
              const maxRetries = 10;
              let retries = 0;
              const interval = setInterval(() => {
                const t = localStorage.getItem('fbToken');
                if (t) {
                  clearInterval(interval);
                  ctx.manualLoginPayment(t, payment_status);
                } else if (++retries >= maxRetries) {
                  clearInterval(interval);
                  this.authService.errorHandlerLogs('fbToken not found after max retries in handlePaymentStatusReceived');
                  this.handleSuccessPayment(payment_status, ctx);
                }
              }, 1000);
            }
          } else {
            this.handleSuccessPayment(payment_status, ctx);
          }
        })
        .catch((error) => {
          this.authService.errorHandlerLogs('Error getting current user in handlePaymentStatusReceived:', error);
          this.handleSuccessPayment(payment_status, ctx);
        });
      return;
    }
    this.basePaymentService.showToast(
      '',
      String(payment_status?.result?.description ?? 'Something went wrong!'),
      'Error'
    );
    ctx.resetPaymentState();
  }
}
